import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Slide,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RequestInfoCard from './RequestInfoCard';

import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import { useAIAssistantContext } from '../../contexts/AIAssistantContext';
import { getScreenNameByPath } from '../../config/navigation';
import { useLocation } from 'react-router-dom';

const sortByLastModified = (rows) => {
  let sortedRows = [...rows].sort((a, b) => {
    const aDate = new Date(a.lastModifiedDate || a.createdDate || 0).getTime();
    const bDate = new Date(b.lastModifiedDate || b.createdDate || 0).getTime();
    return bDate - aDate;
  })
  const priorityIds = ["20549", "20554"];
  const filteredRows = sortedRows?.filter(row =>
    priorityIds.includes(row?.requestId)
  );
  const remainingRows = sortedRows?.filter(row =>
    !priorityIds.includes(row?.requestId)
  );
  return [...filteredRows, ...remainingRows];
};

const getRequestRowKey = (row) => String(row?.requestId || row?.request_id || row?.id || '');
const DESKTOP_TABLE_CHROME = 184;
const APPROX_TABLE_ROW_HEIGHT = 50;
const DEFAULT_TABLE_PAGE_SIZE = 10;

const OpenRequestsInbox = ({ title, subtitle }) => {
  const navigate = useNavigate();
  const { loading, error, data, execute } = useAPICall();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [isManualPageSize, setIsManualPageSize] = useState(false);
  const [tableHeight, setTableHeight] = useState(0);
  const tableBoxRef = useRef(null);
  const inspectorRef = useRef(null);
  const tableInstanceRef = useRef(null);
  const location = useLocation();
  const { registerPayloadGenerator, clearPayloadGenerator } = useAIAssistantContext() || {};

  const fetchOpenRequests = useCallback(async () => {
    try {
      await execute(API_CONFIG.inbox.getUserRequests, null, {
        errorMessage: 'Unable to load open requests.',
        retries: 1,
      });
    } catch {
    }
  }, [execute]);

  useEffect(() => {
    fetchOpenRequests();
  }, [fetchOpenRequests]);

  const rawRequests = useMemo(() => {
    if (Array.isArray(data?.dcrRequests)) return data.dcrRequests;
    if (Array.isArray(data?.data?.dcrRequests)) return data.data.dcrRequests;
    if (Array.isArray(data)) return data;
    return [];
  }, [data]);

  const noOpenRequestsFlag = useMemo(
    () => data?.status === 'no available dcr request' || rawRequests.length === 0,
    [data, rawRequests.length]
  );

  const sortedRequests = useMemo(() => sortByLastModified(rawRequests), [rawRequests]);
  const selectedRequestKey = useMemo(() => getRequestRowKey(selectedRequest), [selectedRequest]);
  const paginatedRequests = useMemo(() => {
    const startIndex = (tablePage - 1) * tablePageSize;
    return sortedRequests.slice(startIndex, startIndex + tablePageSize);
  }, [sortedRequests, tablePage, tablePageSize]);

  useEffect(() => {
    // Intentionally left empty to prevent auto-selecting the first row,
    // so the inspector drawer stays closed by default.
  }, [sortedRequests, noOpenRequestsFlag, selectedRequest]);

  useEffect(() => {
    if (registerPayloadGenerator) {
      registerPayloadGenerator(async () => {
        const screenName = getScreenNameByPath(location.pathname) || 'Open Requests Inbox';
        let records = [];
        if (tableInstanceRef.current) {
          records = tableInstanceRef.current.getRowModel().rows.map(r => r.original);
        } else {
          records = paginatedRequests;
        }

        return {
          screen: screenName,
          page_context:
          {
            current_data: records,
            selected_request: selectedRequest || {},
            selected_count: selectedRequest ? 1 : 0,
            current_count: records?.length,
            overall_count: sortedRequests.length
          }
        };
      });
    }
    return () => {
      if (clearPayloadGenerator) clearPayloadGenerator();
    };
  }, [registerPayloadGenerator, clearPayloadGenerator, paginatedRequests, location.pathname, selectedRequest, sortedRequests.length]);

  useEffect(() => {
    if (typeof window === 'undefined' || !tableBoxRef.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const element = tableBoxRef.current;
    const updateHeight = () => {
      setTableHeight(Math.round(element.getBoundingClientRect().height));
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(element);

    return () => observer.disconnect();
  }, [paginatedRequests, selectedRequest]);


  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedRequests.length / tablePageSize));
    if (tablePage > maxPage) {
      setTablePage(maxPage);
    }
  }, [sortedRequests.length, tablePage, tablePageSize]);

  const openRequestDetailsPage = (row) => {
    if (!row) return;
    const reqId = row.requestId || row.request_id || row.id;
    if (!reqId) return;
    if (row.mdmAccountType && row.mdmAccountType !== 'HCP') {
      navigate(`/hco-dcr?reqId=${reqId}`);
    } else {
      navigate(`/hcp-dcr?reqId=${reqId}`);
    }
  };

  const renderStatusChip = (request) => {
    const statusText = request.OnekeyVRStatus
      ? `OnekeyVR : ${request.OnekeyVRStatus}${request.mdmVrStatus === 'closed pending VR' ? ' | Closed (Pending VR)' : ''}`
      : request.mdmVrStatus === 'closed pending VR'
        ? 'Closed (Pending VR)'
        : 'Open';

    const isClosed = statusText.toLowerCase().includes('closed');
    const isError = statusText.toLowerCase().includes('error') || statusText.toLowerCase().includes('failed');

    return (
      <Chip
        label={statusText}
        size="small"
        sx={{
          height: 24,
          borderRadius: '8px',
          fontWeight: 500,
          
          backgroundColor: isClosed ? alpha('#10b981', 0.1) : isError ? alpha('#ef4444', 0.1) : alpha('#3b82f6', 0.1),
          color: isClosed ? '#059669' : isError ? '#dc2626' : '#1d4ed8',
          border: '1px solid',
          borderColor: isClosed ? alpha('#10b981', 0.2) : isError ? alpha('#ef4444', 0.2) : alpha('#3b82f6', 0.2),
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          '& .MuiChip-label': { px: 1 }
        }}
      />
    );
  };

  const softSearchSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f8fafc',
      borderRadius: '10px',
      transition: 'all 0.2s',
      '& fieldset': { borderColor: '#e2e8f0' },
      '&:hover fieldset': { borderColor: '#cbd5e1' },
      '&.Mui-focused fieldset': { borderColor: '#1E88E5', borderWidth: '1.5px' },
      '&.Mui-focused': { backgroundColor: '#fff' }
    },
    '& .MuiOutlinedInput-input': { py: 1,  }
  };



  return (
    <Box
      sx={{
        pb: 1,
        px: { xs: 1, sm: 1, lg: 0 },
        width: '100%',
      }}
    >
      <Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            {/* Table Section */}
            <Box
              sx={{
                width: selectedRequest ? '72%' : '100%',
                transition: 'width 0.3s ease',
                minWidth: 0,
              }}
            >

            <Box ref={tableBoxRef} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <GlobalMaterialTable
                state={{ isLoading: loading }}
                emptyMessage="No Available Requests"
                tableInstanceRef={tableInstanceRef}
                columns={[
                  {
                    header: 'MDM ID',
                    id: 'mdmId',
                    accessorFn: (row) => row.mdmId || '-',
                    size: 130,
                  },
                  {
                    header: 'Name',
                    id: 'name',
                    accessorFn: (row) => row.accountName || '-',
                  },
                  // {
                  //   header: 'Request Type',
                  //   id: 'requestType',
                  //   accessorFn: (row) => (row.requestType ? row.requestType.split('_')[0] : '-'),
                  //   size: 140,
                  // },
                   {
                    header: 'Requestor Name',
                    id: 'requestorName',
                    accessorFn: (row) => row?.ownerDetails?.name || '-',
                    size: 140,
                  },
                   {
                    header: 'DCR ID',
                    id: 'dcrId',
                    accessorFn: (row) => row.requestId || '-',
                    size: 140,
                  },
                  {
                    header: 'Record Type',
                    id: 'recordType',
                    accessorFn: (row) => (row.recordType ? row.recordType.split('_')[0] : '-'),
                    size: 140,
                  },
                  {
                    header: 'Source',
                    id: 'source',
                    accessorFn: (row) => row.source || '-',
                    size: 130,
                  },
                  {
                    header: 'Status',
                    id: 'status',
                    size: 110,
                    Cell: ({ row }) => renderStatusChip(row.original)
                  },
                ]}
                data={paginatedRequests}
                enablePagination={true}
                enableBottomToolbar={true}
                page={tablePage}
                pageSize={tablePageSize}
                rowCount={sortedRequests.length}
                onPageChange={setTablePage}
                onPageSizeChange={(newSize) => {
                  setIsManualPageSize(true);
                  setTablePageSize(newSize);
                }}
                tableTitle={title}
                tableCount={sortedRequests.length}
                muiSearchTextFieldProps={{
                  size: 'small',
                  placeholder: 'Search inbox...',
                  sx: softSearchSx,
                }}
                muiToolbarAlertBannerProps={{ sx: { borderRadius: '8px' } }}
                muiTableBodyRowProps={({ row }) => {
                  const rowKey = getRequestRowKey(row.original);
                  const isSelected =
                    selectedRequestKey &&
                    rowKey &&
                    selectedRequestKey === rowKey;
                  return {
                    onClick: () => setSelectedRequest(row.original),
                    onDoubleClick: () => openRequestDetailsPage(row.original),
                    sx: {
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#eaf4ff' : 'inherit',
                      '&:hover': { backgroundColor: isSelected ? '#dbeafe' : '#f1f5f9', },
                    },
                  };
                }}
                muiTableContainerProps={{
                  sx: {
                    minHeight: 0,
                  }
                }}
              />
            </Box>
         </Box>
          {selectedRequest && (
            <Slide direction="left" in={!!selectedRequest} mountOnEnter unmountOnExit>
              <Box
                  sx={{
                    width: '28%',
                    minWidth: 320,
                    flexShrink: 0,
                  }}
                >
                <RequestInfoCard
                  ref={inspectorRef}
                  selectedRequest={selectedRequest}
                  onProcessRequest={openRequestDetailsPage}
                  onClose={() => setSelectedRequest(null)}
                  maxHeight={paginatedRequests.length >= 10 && tableHeight ? `${tableHeight}px` : undefined}
                  isShortTable={paginatedRequests.length < 10}
                />
              </Box>
            </Slide>
          )}
        </Box>
      </Box>
    </Box>
  );
};

OpenRequestsInbox.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

OpenRequestsInbox.defaultProps = {
  subtitle: '',
};

export default OpenRequestsInbox;
